import { track } from '@marketplace/helpers/segment/index.js'
import cloneDeep from 'lodash/cloneDeep'
import { v4 as uuidv4 } from 'uuid'

export default ({ store, $tracking, app }, inject) => {
    const impressionInstance = {}
    const impressionDelay = 3000

    const impression = {
        /**
         * Creates a unique impression instance and generates a one-time UUID for the tracked object (eg. a slider)
         * If an initialPayload is delivered track the list of Ids in a separate event
         *
         * @param {string} identifier
         * @param {object} tracking Extra tracking info (not used atm)
         * @param {Array} initialPayload
         */
        start: (identifier, tracking, initialPayload = []) => {
            impressionInstance[identifier] = {
                timer: null,
                requestId: uuidv4(),
                tracking,
            }
        },

        /**
         * When an impressed card gets seen in viewport its tracking object is sent here
         * Any previous timer for sending data is reset
         * Data is saved to vuex
         * Timer is started
         * if timer expires it triggers the preparations to send to segment
         *
         * @param {object} payload
         */
        save: (payload) => {
            const currentImpression = impressionInstance[payload.source]

            if (!currentImpression) {
                return
            }

            clearTimeout(currentImpression.timer)

            store.commit('impressions/updateImpressionList', payload)

            currentImpression.timer = setTimeout(() => {
                impression.prepare(payload.source)
            }, impressionDelay)
        },

        /**
         * Fetches the saved data from Vuex
         * Processes it into a form that DS wants
         * Moves processed object to the actual tracking method
         *
         * @param {string} identifier
         */
        prepare: (identifier) => {
            const impressions = cloneDeep(
                store.getters['impressions/getImpressedItems']
            )
            const selectedImpression = impressions[identifier]
            const impressionSettings = impressionInstance[identifier]

            if (!impressionSettings || !impressionSettings?.tracking) {
                return
            }

            const requestId = impressionSettings.requestId
            const { idPrefix, ...rest } = impressionSettings.tracking

            const config = {
                listings: {
                    ids: 'listingIds',
                    indexPrefix: 'listings',
                },
                wall: {
                    ids: 'listingIds',
                    indexPrefix: 'wall',
                },
                storefronts: {
                    ids: 'storefrontIds',
                    indexPrefix: 'storefronts',
                },
            }

            if (!config[idPrefix]) {
                return
            }

            impression.trackItem(
                {
                    requestId,
                    ...rest,
                    [config[idPrefix].ids]: selectedImpression.map(
                        (item) => item.id
                    ),
                    [config[idPrefix].indexPrefix]: selectedImpression.map(
                        (item) => {
                            const position =
                                item.tracking?.position || 'unknown'
                            return `${position}:${item.id}`
                        }
                    ),
                },
                identifier
            )
        },

        /**
         * Accepts a prepared object for tracking to segment for DS
         * Passes the identifier of the impression
         * Tracks to $tracking
         * Destroys the seen object after tracking
         *
         * @param {object} payload
         * @param {string} identifier
         */
        trackItem: (payload, identifier) => {
            const { eventName, eventCategory, ...tracking } = payload

            if (!window.Store?.accountId) {
                return
            }

            track(`${eventName} Results Viewed`, {
                category: eventCategory,
                ...tracking,
            })

            impression.clear(identifier)
        },

        /**
         * Clears the timer for this impression instance
         * Clears all saved data in vuex for this impression instance
         *
         * @param {string} identifier
         */
        clear: (identifier) => {
            clearTimeout(impressionInstance[identifier].timer)
            store.commit('impressions/clearImpressionList', identifier)
        },
    }

    inject('impression', impression)
}
